// src/components/CarrierAnswerDelays.js
// « Délais de réponse » of one carrier (Kevin 05/11): how long the carrier
// takes to give its DEFINITIVE answer — a decision, the parcel located or
// returned, the requested action done — counted from our first request, in
// business days, over the account's last 12 months: all motifs, then per
// motif. GET /api/logistics/carriers/{id}/answer-delays (Backend
// app/services/carrier_answer_delays.py). A delay shows only when the row
// reaches the sample threshold the API returns (`shown`, `min_answered`);
// cases still without an answer count as « at least » their wait (the median
// is a Kaplan–Meier estimate), their share is shown next to the count.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Timer } from 'lucide-react';
import { Card, Meter } from '../design/ui';
import { formatShortDate } from '../utils/dates';
import './CarrierAnswerDelays.css';

// undefined while loading, null when unavailable, the payload otherwise.
export const useCarrierAnswerDelays = (carrierId) => {
  const [data, setData] = useState(undefined);
  const load = useCallback(async () => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
    if (!token || !carrierId) {
      setData(null);
      return;
    }
    try {
      const response = await axios.get(`/api/logistics/carriers/${carrierId}/answer-delays`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data && typeof response.data === 'object' ? response.data : null);
    } catch {
      setData(null);
    }
  }, [carrierId]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
};

export const hasDelayRows = (data) => Boolean(
  data && ((data.overall && data.overall.cases > 0) || (data.topics || []).some((row) => row.cases > 0)),
);

// « 9 j ouvrés », « 1 j ouvré », « < 1 j ouvré » — the API gives tenths of days.
export const formatBusinessDays = (value, t) => {
  if (value === null || value === undefined) return '—';
  if (value < 1) return t('carrierDelays.lessThanOneDay', '< 1 j ouvré');
  const count = Math.round(value);
  return count === 1
    ? t('carrierDelays.daysOne', '{{count}} j ouvré', { count })
    : t('carrierDelays.daysMany', '{{count}} j ouvrés', { count });
};

const formatShare = (share, lang) => {
  if (share === null || share === undefined) return '—';
  return `${Math.round(share * 100).toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR')} %`;
};

const CarrierAnswerDelays = ({ data, carrierName }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n?.language || 'fr';
  if (!hasDelayRows(data)) return null;
  const rows = [
    ...(data.overall ? [{ ...data.overall, label: t('carrierDelays.allTopics', 'Tous motifs'), isOverall: true }] : []),
    ...(data.topics || []).map((row) => ({ ...row, label: row.topic_label || row.topic })),
  ];
  const longest = Math.max(1, ...rows.filter((row) => row.shown).map((row) => row.median_business_days || 0));
  const minAnswered = data.min_answered || 10;

  return (
    <Card
      icon={Timer}
      title={t('carrierDelays.title', 'Délai de réponse définitive')}
      description={t(
        'carrierDelays.description',
        'Temps entre notre première demande à {{name}} et sa réponse définitive (décision, colis localisé ou retourné, action faite), en jours ouvrés, sur les 12 derniers mois de ce compte.',
        { name: carrierName || data.carrier },
      )}
      flush
      footerNote={[
        data.as_of && t('carrierDelays.asOf', 'Calculé le {{date}}', { date: formatShortDate(data.as_of, lang) }),
        t('carrierDelays.threshold', 'affiché à partir de {{count}} dossiers répondus', { count: minAnswered }),
        t('carrierDelays.openCases', 'un dossier encore sans réponse compte comme « au moins » son attente'),
        t('carrierDelays.agentUse', 'l’agent annonce ce délai dans son premier message au client quand ce transporteur est contacté'),
      ].filter(Boolean).join(' · ')}
      className="carrier-delays"
    >
      <div className="u-table-scroll">
        <table className="u-table carrier-delays__table">
          <thead>
            <tr>
              <th className="carrier-delays__col-topic">{t('carrierDelays.colTopic', 'Motif')}</th>
              <th className="carrier-delays__col-median">{t('carrierDelays.colMedian', 'Délai habituel')}</th>
              <th className="carrier-delays__col-p75">{t('carrierDelays.colP75', '3 dossiers sur 4')}</th>
              <th className="carrier-delays__col-mean">{t('carrierDelays.colMean', 'Moyenne')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const casesLine = t('carrierDelays.casesLine', '{{cases}} dossiers · {{share}} sans réponse', {
                cases: row.cases, share: formatShare(row.censored_share, lang),
              });
              return (
                <tr key={row.topic} className={row.isOverall ? 'carrier-delays__row--overall' : undefined}>
                  <td title={row.label}>
                    <span className="carrier-delays__label">{row.label}</span>
                    <span className="u-cell-sub">{casesLine}</span>
                  </td>
                  {row.shown ? (
                    <>
                      <td title={t('carrierDelays.medianTitle', 'Médiane : la moitié des dossiers ont leur réponse définitive avant ce délai')}>
                        <span className="carrier-delays__value">{formatBusinessDays(row.median_business_days, t)}</span>
                        <Meter
                          value={row.median_business_days || 0}
                          max={longest}
                          label={t('carrierDelays.colMedian', 'Délai habituel')}
                        />
                      </td>
                      <td title={t('carrierDelays.p75Title', 'Trois dossiers sur quatre ont leur réponse définitive avant ce délai')}>
                        {row.p75_business_days === null || row.p75_business_days === undefined
                          ? <span className="u-muted">{t('carrierDelays.p75Unknown', 'non estimable')}</span>
                          : t('carrierDelays.under', 'sous {{days}}', { days: formatBusinessDays(row.p75_business_days, t) })}
                      </td>
                      <td title={t('carrierDelays.meanTitle', 'Moyenne des dossiers déjà répondus (les longs dossiers ouverts n’y sont pas)')}>
                        {formatBusinessDays(row.mean_business_days, t)}
                        <span className="u-cell-sub">
                          {t('carrierDelays.answeredLine', '{{count}} répondus', { count: row.answered })}
                        </span>
                      </td>
                    </>
                  ) : (
                    <td colSpan={3}>
                      <span className="u-muted">
                        {t('carrierDelays.notEnough', 'Pas assez de dossiers : {{answered}} répondus, {{min}} nécessaires', {
                          answered: row.answered, min: minAnswered,
                        })}
                      </span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default CarrierAnswerDelays;
